import { Trans } from '@lingui/react';
import { Alert } from '@startuptools/ui/Alert';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { MeetingSummonsCoverSheet } from './MeetingSummonsCoverSheet';
import { type ComponentProps, Suspense, useState } from 'react';
import { DocumentCreateDialog } from '@startuptools/business-ui/document/DocumentCreateDialog';
import { type DisplayDocument, Document } from '@startuptools/business-ui/document/Document';
import { apiClient } from '../../../../common/http';
import { DocGenRelationName } from '@startuptools/common/doc-gen';
import { useDeleteFileMutation } from '../../../../graphql/react-operations';
import { GqlDocSystemTagKind, type GqlMeeting } from '../../../../graphql/base-types.graphql';

const MeetingUploadsInner = ({
  meeting,
  isSummons = false,
  refetch,
}: {
  meeting: Pick<GqlMeeting, 'id'> & { meetingAppendices: DisplayDocument[] };
  isSummons?: boolean;
  showSummonsAttachments?: boolean;
  refetch: () => void;
}) => {
  const companyId = useCompanyId();
  const [showUploadDialog, setShowUploadDialog] = useState(false);

  // eslint-disable-next-line sut/loading-mutation
  const [doDelete] = useDeleteFileMutation();

  return (
    <>
      <Stack gap={2}>
        {isSummons && (
          <>
            <Typography level="title-lg">
              <Trans id="Cover Sheet" />
            </Typography>
            <MeetingSummonsCoverSheet meeting={meeting} refetch={refetch} />
          </>
        )}
        <Typography level="title-lg">
          <Trans id="Appendices" />
        </Typography>
        <Stack gap={1}>
          <Box>
            <Button onClick={() => setShowUploadDialog(true)} variant="outlined">
              <Trans id="Add Meeting Appendix" />
            </Button>
          </Box>
          {isSummons && (
            <Alert variant="outlined">
              <InfoOutlinedIcon />
              <Trans id="These appendices will be appended to the summons and meeting minutes. If you want to add other attachments to only the summons email, you can do that below" />
            </Alert>
          )}
        </Stack>
        {meeting.meetingAppendices.map(doc => {
          return (
            <Document
              key={doc.id}
              document={doc}
              onDelete={async () => {
                await doDelete({ variables: { companyId, id: doc.id } });
                refetch();
              }}
            />
          );
        })}
      </Stack>
      {showUploadDialog && (
        <DocumentCreateDialog
          mode="upload"
          onClose={() => setShowUploadDialog(false)}
          upload={async ({ file }) => {
            const relation = {
              relation_id: meeting.id,
              relation_name: DocGenRelationName.MeetingId,
            };
            await apiClient.uploadFile(
              file,
              file.name,
              companyId,
              relation,
              false,
              GqlDocSystemTagKind.MeetingAppendix,
            );
            refetch();
            setShowUploadDialog(false);
          }}
          showExistingDocWarning={false}
          showSigningWarning={false}
          allowDigitalSignature={false}
        />
      )}
    </>
  );
};

export const MeetingUploads = (props: ComponentProps<typeof MeetingUploadsInner>) => {
  return (
    <Suspense>
      <MeetingUploadsInner {...props} />
    </Suspense>
  );
};
